import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "./api";

export const DEFAULT_NAME = "ForgeOps";
export const DEFAULT_BRAND = "#4A6B53";
export const DEFAULT_LABELS = { ny_tab: "Budget Jobs", my_cut: "MY Cut", ny_location: "NYC" };

export const useBranding = () => useQuery({ queryKey: ["branding"], queryFn: () => api("/branding"), staleTime: 60_000 });
export const useStatuses = () => useQuery({ queryKey: ["statuses"], queryFn: () => api("/statuses") });
export const useEmployees = (enabled = true) => useQuery({ queryKey: ["employees"], queryFn: () => api("/employees"), enabled });
export const useKastle = () => useQuery({ queryKey: ["kastle"], queryFn: () => api("/kastle") });
export const useCustomers = () => useQuery({ queryKey: ["customers"], queryFn: () => api("/customers") });
export const useParts = (enabled = true) => useQuery({ queryKey: ["parts"], queryFn: () => api("/parts"), enabled });
export const useSuggestions = (kind) =>
  useQuery({ queryKey: ["suggestions", kind], queryFn: () => api(`/suggestions/${kind}`), staleTime: 30_000, enabled: !!kind });

export function useLabels(enabled = true) {
  const q = useQuery({ queryKey: ["labels"], queryFn: () => api("/settings/labels"), enabled, staleTime: 60_000 });
  return q.data ?? DEFAULT_LABELS;
}

/** Invalidate the job lists after any change to a work order. */
export function useRefreshJobs() {
  const qc = useQueryClient();
  return (wo) => {
    if (wo?.id) qc.setQueryData(["work-order", wo.id], wo);
    qc.invalidateQueries({ queryKey: ["work-orders"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
}

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgbToHex = (rgb) => `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("")}`;
const mix = (hex, target, amt) => rgbToHex(hexToRgb(hex).map((v) => v + (target - v) * amt));
const luminance = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const isValidHex = (hex) => /^#[0-9A-Fa-f]{6}$/.test(hex ?? "");

export function applyBrand(hex) {
  const h = isValidHex(hex) ? hex : DEFAULT_BRAND;
  const s = document.documentElement.style;
  s.setProperty("--wf-brand", h);
  s.setProperty("--wf-onbrand", luminance(h) > 0.45 ? "#1C1C1E" : "#FFFFFF");
  s.setProperty("--wf-brand2", mix(h, 255, 0.3));
  s.setProperty("--wf-soft", mix(h, 255, 0.88));
  s.setProperty("--wf-softink", mix(h, 0, 0.45));
}

/** Company brand color set by the admin in Branding — applied app-wide. */
export function BrandColorSync() {
  const b = useBranding();
  const color = b.data?.brand_color;
  useEffect(() => {
    applyBrand(color);
  }, [color]);
  return null;
}
